import { Component, useEffect, useRef, useState, type ReactNode } from 'react';
import { ErrorBoundary as ReactErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { ConnectionError, type ConnectionErrorType } from './ConnectionError';
import { ConnectingScreen } from './ConnectingScreen';
import { healthCheckService, type HealthStatus } from '@/services/healthCheck.service';
import { ErrorPhaseManager, type ErrorPhase } from '@/services/errorPhase.manager';
import { errorCoordinator } from '@/services/errorCoordinator';

// Phase durations (ms)
const PHASE_1_DURATION = 45_000; // Silent spinner
const PHASE_2_DURATION = 45_000; // "Connecting..." modal

// Retry intervals per phase (ms)
const AGGRESSIVE_RETRY_MS = 3_000;  // Phases 1 & 2
const RELAXED_RETRY_MS = 10_000;    // Phase 3

function mapHealthStatusToErrorType(status: HealthStatus): ConnectionErrorType | null {
  switch (status) {
    case 'backend_down':
      return 'backend';
    case 'database_down':
      return 'database';
    case 'network_error':
      return 'network';
    case 'healthy':
    default:
      return null;
  }
}

function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const [phase, setPhase] = useState<ErrorPhase>('silent');
  const phaseManagerRef = useRef<ErrorPhaseManager | null>(null);
  const retryRef = useRef<ReturnType<typeof setInterval> | null>(null);

  let errorType: ConnectionErrorType = 'backend';
  if (error.message?.toLowerCase().includes('network')) {
    errorType = 'network';
  } else if (
    error.message?.toLowerCase().includes('database') ||
    error.message?.toLowerCase().includes('timeout')
  ) {
    errorType = 'database';
  }

  useEffect(() => {
    const pm = new ErrorPhaseManager({
      phase1Duration: PHASE_1_DURATION,
      phase2Duration: PHASE_2_DURATION,
      onPhaseChange: (p) => {
        setPhase(p);
        if (p === 'error') {
          // Slow down retries in phase 3
          if (retryRef.current) clearInterval(retryRef.current);
          retryRef.current = setInterval(() => resetErrorBoundary(), RELAXED_RETRY_MS);
        }
      },
    });
    phaseManagerRef.current = pm;
    pm.start();

    // Aggressive retries during phases 1 & 2
    retryRef.current = setInterval(() => resetErrorBoundary(), AGGRESSIVE_RETRY_MS);

    return () => {
      pm.reset();
      if (retryRef.current) clearInterval(retryRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  switch (phase) {
    case 'silent':
      return (
        <div className="flex items-center justify-center h-dvh">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
        </div>
      );
    case 'warning':
      return <ConnectingScreen onRetry={resetErrorBoundary} />;
    case 'error':
      return (
        <ConnectionError
          type={errorType}
          onRetry={resetErrorBoundary}
          autoRetryInterval={RELAXED_RETRY_MS}
        />
      );
  }
}

interface HealthMonitorState {
  healthStatus: HealthStatus;
  phase: ErrorPhase;
}

class HealthMonitor extends Component<{ children: ReactNode }, HealthMonitorState> {
  private unsubscribe?: () => void;
  private phaseManager: ErrorPhaseManager;
  private retryInterval: ReturnType<typeof setInterval> | null = null;

  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = {
      healthStatus: healthCheckService.getLastStatus(),
      phase: 'silent',
    };
    this.phaseManager = new ErrorPhaseManager({
      phase1Duration: PHASE_1_DURATION,
      phase2Duration: PHASE_2_DURATION,
      onPhaseChange: (phase) => {
        this.setState({ phase });
        if (phase === 'error') {
          this.startAggressiveRetries(RELAXED_RETRY_MS);
        }
      },
    });
  }

  componentDidMount() {
    healthCheckService.startMonitoring(60000);
    this.unsubscribe = healthCheckService.subscribe((status) => {
      const wasHealthy = this.state.healthStatus === 'healthy';
      const isHealthy = status === 'healthy';

      if (!isHealthy && wasHealthy) {
        // Just went unhealthy — start phase progression + aggressive retries
        this.setState({ healthStatus: status });
        this.phaseManager.start();
        errorCoordinator.setHealthMonitorActive(true);
        this.startAggressiveRetries(AGGRESSIVE_RETRY_MS);
      } else if (isHealthy) {
        // Recovered — reset everything
        this.recover();
      } else {
        // Still unhealthy — just update status (phase continues)
        this.setState({ healthStatus: status });
      }
    });
  }

  componentWillUnmount() {
    this.unsubscribe?.();
    this.phaseManager.reset();
    this.clearRetryInterval();
    healthCheckService.stopMonitoring();
    errorCoordinator.setHealthMonitorActive(false);
  }

  private recover() {
    this.phaseManager.reset();
    this.clearRetryInterval();
    this.setState({ healthStatus: 'healthy', phase: 'silent' });
    errorCoordinator.setHealthMonitorActive(false);
    // Restore normal 60s polling
    healthCheckService.stopMonitoring();
    healthCheckService.startMonitoring(60000);
  }

  private clearRetryInterval() {
    if (this.retryInterval) {
      clearInterval(this.retryInterval);
      this.retryInterval = null;
    }
  }

  private startAggressiveRetries(intervalMs: number) {
    if (this.retryInterval) clearInterval(this.retryInterval);

    // Stop the default 60s polling — we take over
    healthCheckService.stopMonitoring();

    this.retryInterval = setInterval(async () => {
      const result = await healthCheckService.checkHealth();
      if (result.status === 'healthy') {
        this.recover();
      }
    }, intervalMs);
  }

  handleRetry = async () => {
    const result = await healthCheckService.checkHealth();
    if (result.status === 'healthy') {
      this.recover();
    }
  };

  render() {
    const errorType = mapHealthStatusToErrorType(this.state.healthStatus);

    if (!errorType) {
      return this.props.children;
    }

    switch (this.state.phase) {
      case 'silent':
        return (
          <div className="flex items-center justify-center h-dvh">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        );

      case 'warning':
        return <ConnectingScreen onRetry={this.handleRetry} />;

      case 'error':
        return (
          <ConnectionError
            type={errorType}
            onRetry={this.handleRetry}
            autoRetryInterval={RELAXED_RETRY_MS}
          />
        );
    }
  }
}

export function AppErrorBoundary({ children }: { children: ReactNode }) {
  const handleError = (error: Error, errorInfo: { componentStack?: string }) => {
    console.error('Application Error:', error);
    if (errorInfo.componentStack) {
      console.error('Component Stack:', errorInfo.componentStack);
    }
  };

  const handleReset = () => {
    healthCheckService.checkHealth();
  };

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={handleError}
      onReset={handleReset}
    >
      <HealthMonitor>{children}</HealthMonitor>
    </ReactErrorBoundary>
  );
}
